import { boundarySource } from './boundary-journal.js';
// janus-engine/ability/context.ts — the runtime state of ONE browser-resolved ability use (the generic pipeline, 2026-09-26).
//
// ⭐ PORTING CONTRACT (read before adding a branch). The ability pipeline is a FAITHFUL TRANSLITERATION of PHP janus()
// (includes/functions/janus/core/janus.php and the handlers it reaches): the same function names in camelCase, the same order,
// the same branch conditions, the same log rows. It is NOT a redesign — parity is proven by the replay oracle
// (tools/test/janus-ability-oracle-census.mjs), which compares every roll row, encounter_creatures column and status row PHP wrote.
//   · A branch that is not ported yet calls handBack('<php_function>:<branch>') — it throws, the whole use hands back to PHP,
//     and the census buckets it by that reason. NEVER guess a branch: an unported branch that silently does nothing would make the
//     browser write a WRONG result the moment the lane serves it.
//   · Every draw goes through ctx.roll (the RollSource). The oracle replays PHP's taped draws, so an extra or missing draw desyncs
//     every later face and shows up as a mismatch — order matters exactly as in PHP.
//   · Member rows are CLONES (ctx.members); mutate them freely, the verdict diffs encounter_creatures against the originals.

import { type RollSource } from '../actions/roll-source.js';
import type { AdvDisTraceEntry } from '../actions/weapon-attack-commit.js';
import { orbQueueText } from './orbs/orb-queue.js';
import { ecLean, EC_LEAN_ZERO } from '../compile/ec-lean-reader.js';
export { ecLean } from '../compile/ec-lean-reader.js';

import { encounterCreatureField, posCoord } from '../../shared/array-types';

export type Row = Record<string, unknown>;

/** Thrown by any branch the port does not model yet. Caught once, at resolveAbilityUse. */
export { HandBack, handBack } from './hand-back.js';
import { handBack } from './hand-back.js';

/** A catalog definition this cast needs is not held: named WITH its id (`<reason>:<ID>`) so the lane fetches it and resolves again; an
 *  unreadable id keeps the bare reason. */
export function definitionNotHeld(reason: string, id: unknown): never {
  if (Array.isArray(id) && id.length === 1) id = id[0];
  const row = record(id);
  if (row) id = record(row.items)?.item_id ?? row.item_id;
  const ID = typeof id === 'string' ? id.toUpperCase() : '';
  return /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(ID)
    ? handBack(`${reason}:${ID}`) : handBack(reason);
}

/** Thrown where PHP calls display_warning(); exit; — a refusal with PHP's exact message. */
export class Refusal extends Error {
  constructor(public readonly refusal: string) { super(`refusal: ${refusal}`); }
}
export function refuse(message: string): never { throw new Refusal(message); }

/** One roll-log row (insert_rolls_inc.php columns the oracle compares). */
export interface LogRow {
  /** Browser-decided display snapshot for the built-in summon-control log. */
  item_display?: { item_type: 'full_ability'; item_name: 'Dismiss Summon' | 'Recall Summon' }
    | { item_type: 'merchant_haggle' | 'skill'; item_name: string };
  /** Per-row visibility: public (0), GM-only detail (1), secret (2). */
  roll_private?: 0 | 1 | 2;
  roll_type_1: string;
  roll_type_2: string | null;
  roll_item_id: string | null;
  roll_by: string | null;
  roll_to: string | null;
  roll_result: number;
  rolls_full?: unknown[] | string | null;
  /** End Concentration's catalog destination is display data, outside the encounter's creature participants. */
  log_item_target?: { item_id: string; item_name: string; item_type: string };
  /** roll_meta.dice — {dice, die, mod, adv_dis_stacks, dc} */
  dice?: { dice: number; die: number; mod: number; adv_dis_stacks: number; dc: number };
  /** roll_meta.feedback — calc_total_damage_and_apply_to_target's roll_feedback on a damage row (rolled / applied, per type). */
  feedback?: RollFeedback | null;
  /** roll_meta.random_table — the range snapshot a browser-rolled table row decided (janus-engine/rolls/random-table.ts); the commit
   *  records it as posted (purified), never looking the range up. Absent on every other row. */
  random_table?: { effect: string | null; description: string | null; attached_item_id: string | null } | null;
  /** The campaign house rule a row is logged under (insert_roll's adv_dis_trace_json `log_banner: campaign_house_rule` — the crit cascade
   *  d20s, the Maimed-on-crit Fortitude save; lane A7). The commit writes the rule's banner; absent on every other row. */
  house_rule?: CritHouseRule;
}
export type CritHouseRule = 'critical_hits_improved_damage_remarkable' | 'critical_hits_improved_damage_world_shaking' | 'maimed_on_crit_fortitude_save';
/** Local presentation metadata; reaction rows retain their fixed wire-column contract. */
export const rollTraces = new WeakMap<LogRow, readonly AdvDisTraceEntry[]>();
export interface RollFeedbackType { damage_type_id: string; rolled: number; applied: number | null; typed: number; reason: string | null }
export interface RollFeedback { rolled: number; applied: number; verb: 'taken' | 'restored' | 'gained' | 'spent'; reason: string | null; damage_types?: RollFeedbackType[] }

/** One status row created by the use; the browser assigns its identity before commit. */
/** Fresh status rows retain the exact definition used to apply them, without sending definitions on the wire. */
export const pendingStatusDefinitions = new class extends WeakMap<object, Row> {
  override get(row: object): Row | undefined { return super.get(boundarySource(row)); }
  override has(row: object): boolean { return super.has(boundarySource(row)); }
  override set(row: object, definition: Row): this { return super.set(boundarySource(row), definition); }
  override delete(row: object): boolean { return super.delete(boundarySource(row)); }
}();
export interface StatusRow {
  status_id?: string;
  status_item_id: string;
  target: string;
  caster: string;
  status_connected_id: string;
  status_stack: number;
  status_ends_on_target_start_of_turn: number;
  status_ends_on_target_end_of_turn: number;
  status_ends_on_caster_start_of_next_turn: number;
  status_ends_on_caster_end_of_next_turn: number;
  /** status_ends_campaign_{year..second} joined with '-' ('0-0-0-0-0-0' = no timed end). */
  ends: string;
  status_aoe_type?: string | null;
  status_aoe_distance?: number;
  status_aoe_distance_depth?: number;
  status_aoe_distance_height?: number;
  status_aoe_source?: number;
  status_aoe_geometry?: string | null;
  status_origin_point_x?: number;
  status_origin_point_y?: number;
  status_origin_point_z?: number;
  status_distance_point_x?: number;
  status_distance_point_y?: number;
  status_distance_point_z?: number;
  status_origin_id?: string | null;
  status_casting_targets_arr?: string | null;
  status_opposing_team_only?: number;
  status_damage_type?: string | null;
  /** apply_status_effect_inc.php's cast-time snapshot on a re-ticking status: 1 = max, -1 = min, 0 = none. */
  status_forced_roll_extreme?: -1 | 0 | 1;
}

export interface InventoryChange { inventory_id: string; changes: Record<string, unknown>;
  movement?: { x_pos: number; y_pos: number; z_pos: number };
  carve?: { rows: Row[] };
  destruction?: import('../inventory/destroy-object.js').ObjectDestroyCommand }

/** The campaign clock the page holds (campaign_time_*), used for status end times. */
export interface CampaignClock { year: number; month: number; day: number; hour: number; minute: number; second: number }

export interface JanusReaders {
  inventoryTemplates?: () => import('../inventory/compose-size-template').InventoryTemplateSources;
  /** Successful fresh saved-row reads only; cache/roster absence never proves departure. */
  maintainedTargetGone?: (id: string) => boolean;
  /** The floor move lane's current support geometry, used to seat a thrown object below its aim centre. */
  throwLandingWorld?: () => import('../inventory/floor-move.js').RestWorld;
  /** Each lane checks its own recorder before offering and after resolving an independent kill effect. */
  killTriggerPreflightFailure?: (ability: Row) => string | null;
  killTriggerRecorder?: (ctx: JanusCtx) => () => string | null;
  /** Shared by this action's synthetic and real frames; only a fully unservable effect is refused before offering. */
  killTriggerProofs?: Map<string, { served: boolean; failure: string | null }>;
  /** The caster's campaign house rules (crit model, exploding dice, crit cascades) — house-rules.ts campaignHouseRules();
   * null when no campaign row is held. Optional: a reader set without it hands back wherever the rules matter. */
  houseRules?: () => import('./house-rules.js').HouseRules | null;
  /** OPTIONAL (R-197, 2026-10-07): encounter_light_types_present() for this ctx's encounter — the if_light_type_[not_]active gates of a
   *  start-of-turn regeneration passive (regeneration-entry-gate.ts). null = the base light is unknown (PHP fails open); absent = the
   *  host cannot answer → the gate stays a hand-back. `members` = the ctx's CURRENT member clones (a tick's earlier status changes move
   *  light sources — Codex review 2026-10-07 P2); the host patches them over its encounter snapshot. */
  lightTypesPresent?: (holder: Row, members?: readonly Row[]) => string[] | null;
  /** OPTIONAL (lane RX): recompile a member's derived creature after status mutations (member-status-recompile.ts over the C1
   *  possessor compile) with the host's definitions and the member's catalog. Absent = the host cannot → the caller hands back. */
  recompileStatuses?: (row: Row, steps: import('../compile/member-status-recompile.js').MemberStatusStep[], members?: readonly Row[]) => import('../compile/member-status-recompile.js').MemberRecompileResult;
  /** OPTIONAL (fix-bug 2026-10-14): the page scene of a member's light / condition gates for a status recompile (websocket/possessor-scene.ts).
   *  `members` = the use's CURRENT member clones (they replace their snapshot rows). Absent reader = a light- or condition-gated passive
   *  refuses that recompile by name (`unsupported-condition:<kind>`). */
  possessorScene?: (row: Row, members?: readonly Row[]) => import('../compile/member-status-recompile.js').MemberRecompileScene | undefined;
  /** OPTIONAL (lane RX): the ARMED counterspell watches on a caster (encounter_creature_triggers, trigger_type 'counterspell', active,
   *  oldest first) plus the wild-magic surge table's die, as the page fetched them for this use. null / absent = not fetched → an
   *  eligible spell hands back (counterspell.ts). */
  counterspellWatches?: (casterId: string) => CounterspellWatchSet | null;
  /** OPTIONAL (Wizard v9 summons_gain_caster_cantrips — summon-caster-cantrips.ts statCreature): the controller id whose STATS this caster
   *  casts this ability with (an injected cantrip the page decided for this use), else null. Absent = every caster rolls its own stats. */
  summonCantripStatSource?: (casterId: string, abilityId: string) => string | null;
  /** ft3d_viewer_target_visibility_state; null means perception inputs are incomplete. */
  visibility: (viewer: Row, target: Row) => 'visible' | 'touch_located' | 'position_known' | 'unseen' | null;
  /** The scene's saved roster and current point-sight model decide recipients before geometry leaves the committing page. */
  areaVfxRecipients?: (area: import('../../shared/magic-area.js').MagicArea) => string[];
  /** True only after an authoritative complete target compile was fetched. */
  reactionInventoryComplete: (target: Row) => boolean;
  /** Delivered bystander inventory for K4 boundaries only; never certifies a damage witness. */
  bystanderReactionInventoryHeld?: (target: Row) => boolean;
  /** Authoritative dead flag recovered from a full target compile; null when unheld. */
  deadState: (target: Row) => 0 | 1 | null;
  /** layer_value() twin (ftLayerValue). */
  value: (row: Row | null, key: string) => unknown;
  /** layer_has() twin. */
  has: (row: Row | null, key: string) => boolean;
  /** layer_entries() twin (ftLayerEntries). */
  entries: (row: Row | null, key: string) => unknown[];
  /** creature_statuses(row, 'target') with each definition under status_arr. */
  statuses: (row: Row) => Row[];
  /** Full definition {items, item_layer_types} for a catalog id (row catalog → object cache). Null when not held. */
  definition: (id: string) => Row | null;
  /** ft_creature_in_combat() for a member row. */
  inCombat: (encounter: Row, row: Row) => boolean;
  /** The campaign clock at cast time; null when the caster has no campaign context. */
  campaignClock: () => CampaignClock | null;
  /** convert_seconds_to_campaign_time(campaign, seconds) → the advanced clock. */
  addCampaignSeconds: (clock: CampaignClock, seconds: number) => CampaignClock;
  /** new_guid() — status_connected_id etc. (the oracle does not compare minted ids). */
  uuid: () => string;
  /** Page's shared ground placement geometry, including native bodies and inventory obstacles. */
  groundExitPlacement?: (encounter: Row, rider: Row, origin: import('../movement/move-destination.js').Point) => import('../movement/move-destination.js').MoveDestination;
  /** inventory_field() twin — optional; absent → any read needing it hands back rather than guess. */
  inventoryField?: (row: Row | null, key: string) => unknown;
  /** Page's cached inventory lookup for a spell grant's source provenance. */
  resolveInventory?: (id: string) => Row | null;
  inventoryDestruction?: (row: Row) => import('../inventory/destroy-object.js').ObjectDestroyReaders;
  /** OPTIONAL (Session 1 step 2): ft_ground_occupancy_shape(row) for a member a Self follow-aura may reach at cast time
   *  (expand_self_follow_aura_targets_if_needed). null = no footprint (never inside); undefined / absent = unproved → hand back. */
  auraOccupancy?: (row: Row) => import('./aoe/expand-placed-aoe-targets.js').AoeOccupancy | null | undefined;
  /** OPTIONAL CAPABILITY (Session A2 step 4c): the turn-holder's death — turns/end-turn.ts resolveTurnDeath over the rows THIS use has reached
   *  (`encounter` / `members` are the ctx's working clones). Absent → a lethal hit on the turn holder hands back (the caller's commit cannot
   *  write the transition). */
  turnDeath?: (encounter: Row, members: ReadonlyMap<string, Row>, dyingId: string) => import('../turns/end-turn.js').TurnDeathResult;
  spawnTransform?: (ctx: JanusCtx, target: Row, original: Row, request: import('../spawn/row-plan').TransformSpawnRequest) => import('../spawn/row-plan').SpawnRowPlan;
  spawnSummons?: (ctx: JanusCtx, plans: readonly import('./summon').SummonPlan[]) => import('../spawn/row-plan').SpawnRowPlan;
  iceWallSections?: (ctx: JanusCtx, wall: import('../../shared/painted-wall-geometry').PaintedWallGeometry) => import('./aoe/painted-wall-ice-cast').IceWallSections;
  deathResources?: (ctx: JanusCtx, target: Row) => import('../death/compose').DeathResources;
  /** OPTIONAL CAPABILITY (lane A10, OWNER RULING 2026-10-02 — optional triggers are prompt-then-resolve): a PAGE supplies it
   *  (websocket/trigger-consent.ts); absent (an oracle, a census) → every trigger auto-fires as in PHP. ./trigger-consent.ts. */
  triggerConsent?: import('./trigger-consent.js').TriggerConsent;
}

/** One armed counterspell watch (an encounter_creature_triggers row): the watcher is its caster_encounter_creature_id. */
export interface CounterspellWatch { triggerId: string; watcherId: string; itemId: string; createdAt: string }
export interface CounterspellWatchSet { watches: CounterspellWatch[] }
/** Fixed insert/replace decision; the save derives watcher and encounter from the locked actor. */
export interface CounterspellArm { kind: 'counterspell'; triggerId: string; targetId: string; itemId: string; mode: 'arm_counterspell' }
/** What the use's counterspell resolution claimed (the commit re-claims exactly these, atomically): `claimed` = the watches examined in
 *  order (each one PHP's claim_encounter_creature_trigger turned inactive), `resolved` = the watcher that contested (null = all fizzled). */
export interface CounterspellClaims { claimed: string[]; resolved: string | null }

/** apply_damage_to_target() on an OBJECT (an encounter inventory row — R2 asks 3–4, 2026-09-28): what the hit left it with.
 * `destroyed` = v_current reached 0 (incl. ft_inventory_forced_destruction_by_damage_type); the CALLER runs
 * handle_inventory_destroyed_from_damage's twin (R1's planDamageDestruction) — the resolver stops there, as PHP's creature-only
 * tail (triggers, concentration, reactions) is suppressed for a destroyed object. */
export interface ObjectDamageEntry { inventory_id: string; before: { v_current: number; v_temp: number }; v_current: number; v_temp: number;
  destroyed: boolean; forced: boolean; damageTypeIds: string[]; total: number; afterRow: number }
/** An unresolved corpse obligation from a damage frame. Public recorders refuse it before live dice; PHP never executes it. */
export interface DeathTailEntry { target: string; class: 'killed' | 'killed_pc_massive'; massiveLog: boolean; afterRow: number;
  /** OWNER RULING 2026-10-03 (lethal push collisions — lane A11): the creature whose dispatch dealt the kill (apply_death_effect's caster) when it
   *  is not the use's actor — a pushed creature killed by its own nested Take Collision Damage cast. Absent = the actor (or the zone's caster). */
  killer?: string;
  /** …and a RAW kill (apply_half_collision_damage_to_struck: apply_damage_to_target with no damage row): the vitality it took. */
  raw?: number }
/** Historical concentration-tail wire shape. Current browser cascades emit removals/logs and leave this list empty. */
export interface ConcentrationBreak { target: string; statusId: string; afterRow: number }

/** A matched listener decided in the browser, including a gated or declined dispatch. Identity only on the wire. */
export interface StatusTriggerListener {
  reactorId: string;
  abilityId: string;
  trigger: string;
  statusItemId: string;
  pool: 'carrier' | 'status';
  statusId?: string;
}

export interface JanusCtx {
  surfaceReactionFrame?: import('./surface-reactions.js').SurfaceReactionFrame;
  consumedUnitSources?: Set<string>;
  /** Target-use listeners are once per target in a janus frame; nested/later casts have their own set. */
  itemTypeAgainstFired?: Set<string>;
  throwObjectFirstHit?: Row;
  /** Encouraging Spell's successful recipients choose an Opportunity Attack after this cast commits. Never posted as saved state. */
  nextSpellOpportunityOffers?: Set<string>;
  /** Roll-trigger dedup belongs to the effect frame and is restored with a rolled-back effect. */
  cmrtFired?: Set<string>;
  /** Turn commits persist complete Mastery marker adds/removals. */
  masteryMarkers?: boolean;
  /** GM status boundaries persist the primary row first and resolve each trigger frame independently. */
  statusTriggersMuted?: boolean;
  statusTriggerListeners?: StatusTriggerListener[];
  /** Dry-run discrete-pick explorer. Real dispatches draw through roll without this callback. */
  randomPickProbe?: (size: number, drawn: number) => number;
  encounter: Row;
  /** Clones of the encounter's member rows, keyed by upper-case encounter_creature_id. */
  members: Map<string, Row>;
  /** The originals (never mutated) — the verdict's encounter_creatures diff base. */
  originals: ReadonlyMap<string, Row>;
  read: JanusReaders;
  roll: RollSource;
  rows: LogRow[];
  /** Direct-use per-target skip messages, deduped across every dispatch in this request. */
  warnings?: string[];
  statusesAdded: StatusRow[];
  statusesRemoved: Array<{ status_item_id: string; target: string; status_id?: string; caster?: string; before?: Row }>;
  chargesChanged: Array<{ holder: string; item_id: string; before: number; charges: number }>;
  /** Exact temporary pools retired with their granting status; an absent pool is a no-op. */
  chargesRemoved?: Array<{ holder: string; item_id: string }>;
  inventoryChanged: InventoryChange[];
  /** Shared boundary saver persists the inventory damage lane in this context. */
  saveBoundaryInventory?: boolean;
  /** Per-use S21 plans and live sections; dry runs own independent maps. */
  wallRunAoeSections?: Map<string, number[]>;
  /** An instant volley's union is carved once; each burst still damages only its own sections. */
  wallRunAoeAllSections?: Map<string, number[]>;
  wallRunAoeAction?: string;
  wallRunCarved?: Map<string, Row[]>;
  /** Capability: this caller persists form inventory writes for its commit actor. */
  shapeshiftInventory?: import('../statuses/shapeshift-inventory.js').ShapeshiftInventoryWrite[];
  shapeshiftActorId?: string;
  /** One cast's form projections, shared across its dry runs; keys are held member identities and ordered status steps. */
  shapeshiftProjections?: WeakMap<Row, Map<string, import('../compile/member-status-recompile.js').MemberRecompileResult | null>>;
  timedInventory?: import('./timed-finish/inventory.js').TimedInventoryDelta;
  monsterBitRemovals?: import('../inventory/monster-bit-removal.js').MonsterBitRemoval[];
  dyingTail?: Array<{ target: string; killer: string; abilityId: string; afterRow: number }>;
  /** player_characters updates from an ascension skill-point gain. The commit
   * writes these columns by the PC's item_id in the same transaction. */
  playerCharactersChanged?: Array<{ item_id: string; skill_id: string; column: string; before: number; after: number }>;
  /** Shared connection for the statuses written by this Janus cast. */
  statusConnectedId: string | null;
  /** Exact engine frame admitted by the timed finish resolver; nested casts cannot borrow it. */
  timedFinish?: { statusId: string; casterId: string; abilityId: string; variable2Arr: Row | null; summonPoints: unknown[]; castLevel?: number };
  /** Caster-side first fragment extreme by roll type, for apply_status_effect's cast-time tick snapshot. */
  castForcedRollExtreme?: Record<string, 'max' | 'min' | null>;
  /** R-108 push collision (lane A4, 2026-10-01): the target whose damage FRAGMENT is already prepared (its dice drawn — PHP's damage_type_rolls
   *  runs before post_effect_conditions) while the effects run between preparation and application; a damaging push may collide then. */
  damagePreparedTarget?: string | null;
  /** check_post_damage_type_roll_conditions runs at the tail of PHP's DRAW, before post_effect_conditions: each fragment's status gate reads the
   *  target's status rows from BEFORE this use's own effects (damageRollOneTarget's plain path sets it; ecId → rows). */
  fragmentGateStatuses?: Map<string, Row[]> | null;
  /** Members changed by push collision or support-fall damage; prepared fragments re-read their application state. */
  pushCollisionTouched?: Set<string>;
  /** R-108 push collision (lane A5): the targets of the roll dispatch in progress (janusDispatchRollTypes) — a collision that STRIKES another
   *  of them hands back (PHP writes each loop target from its own pre-loop copy, so the struck half damage's fate is write-order). */
  dispatchTargetIds?: ReadonlySet<string>;
  rollPrivate: number;
  /** Tracks provisional kill classification for frame ordering and trigger previews. A complete
   * recorder must refuse a nonempty list until its browser corpse-row plan exists. */
  deathTail?: DeathTailEntry[] | null;
  /** Historical obligation list; current concentration cleanup emits ordinary browser status removals. */
  concentrationBreaks?: ConcentrationBreak[] | null;
  /** Lane A12 (coordinator Round 24 P2): the creatures a RAW hit (the push collision's struck half) already reached this use — recorded on
   *  entry, before the zero-total return, so a second raw hit on one hands back independent of the dice. */
  rawStruck?: Set<string>;
  /** Session A2 step 4c: the turn boundary a lethal hit on the TURN HOLDER makes (read.turnDeath), written by the commit's death tail at that
   *  creature's tail point — one per use (a second turn-holder death hands back). */
  turnTransition?: import('../turns/end-turn.js').TurnDeathTransition | null;
  /** The turn holder this use killed when the boundary decided NO advance (fewer than two combatants) — the commit accepts that death. */
  turnDeathNone?: string | null;
  /** Same CAPABILITY idiom: present = the caller's commit can write a quantity-stack increment on an EXISTING status row
   * (janus_add_status_stack). Absent (turns/ticks.ts) → the increment hands back. */
  statusStacks?: StatusStackChange[];
  /** Same CAPABILITY idiom as deathTail: present (even empty) = the caller persists an OBJECT's damage (v_current / v_temp and a
   * destruction). Absent → an inventory damage target hands back. */
  objectDamage?: ObjectDamageEntry[];
  /** Ordinary ability commits persist the object hit and its browser-decided destruction together. */
  saveObjectDamage?: boolean;
  /** FIFO destruction jobs already drained within this speculative ability frame. */
  destructionPayloadsDrained?: Set<string>;
  /** Browser-decided ammunition consumption/recovery, saved atomically with the ability receipt. */
  weaponInventoryChanges?: import('../actions/weapon-attack-inventory.js').WeaponInventoryChange[];
  /** janus()'s `$trigger` (TRIGGER MODE): a server-started frame — AP / reaction debits no-op (janus_trigger_in_progress()).
   * Set by environmental-payload.ts ('destruction_effect_ability'); absent on a player's own use. */
  trigger?: string | null;
  /** Self-dispatched passive: only its target-status gates read the inciting creature. Restored with the trigger frame. */
  triggerIncitingTarget?: Row | null;
  /** A Last Stand ward held only as a status (TriggerEvent.statusIsCharge, no bucket pool): its frame spends no pool — the status IS the
   *  charge and tryLastStand removes it. Restored with the trigger frame. */
  statusChargeFrame?: string | null;
  /** An instant projectile volley's CHILD dispatch in progress (use_ability_inc.php phase 'child'): that child's own charge pool is
   *  exempt (creature_ability_charge_pool_spends_on_this_use → false). Set by ability-use.ts around each child's janus(). */
  instantChild?: string | null;
  /** ctx.rows index where the CURRENT top-level janus() dispatch began (a volley's projectile) — per-dispatch accumulations (damage_roll's
   *  AoE type_2 rule) look no further back. Absent = 0. */
  dispatchRowStart?: number;
  /** One K4 post-roll passive pass per Janus frame, shared across its targets. */
  k4PostRollFrames?: Set<string>;
  /** Status rows an EARLIER top-level dispatch of this use added (a volley's previous projectiles): PHP reads them as STORED rows in the
   *  next janus() call, so the single-dispatch "pending" rules skip them (isPendingThisDispatch). Absent = every added row is pending. */
  priorDispatchStatuses?: ReadonlySet<StatusRow>;
  /** Lane RX: the counterspell resolution of this use's spell (counterspell.ts) — carried to the verdict for the commit's atomic claim. */
  counterspellClaims?: CounterspellClaims | null;
  counterspellArm?: CounterspellArm;
  /** Wizard v9 spells_cannot_be_counterspelled: armed watches exist on the caster but THIS spell is protected, so none was examined — the
   *  verdict says so (counterspellExempt) and the commit skips its armed-watch match instead of sending the cast back. */
  counterspellExempt?: boolean;
  /** Lane RX: the spell was interrupted (counterspell success or tie) — template_spell_roll's $counterspelled. */
  counterspelled?: boolean;
  /** Successful area onsets, relayed only after the containing verdict commits. */
  areaCasts?: import('../../shared/magic-area.js').AreaCastVfx[];
  /** Present only when this frame's commit persists earned teleport choices. */
  teleportOffers?: import('./movement/ability-teleport.js').AbilityTeleportOffer[];
  /** One grant per successful Janus dispatch, shared by its target passes. */
  teleportFrame?: Set<string>;
  /** Session 1 step 5a: the cast LEVEL a numeric use_ability_variable_1 names on a leveled spell / curse / menta (upcast.ts); absent /
   *  null = cast at the spell's own level (upcast_roll() is a no-op). */
  castLevel?: number | null;
  /** Wizard v9 WP5 (charged-cast.ts): the USE's charged-item source (a charged_spell / charged_ability cast from an inventory row — wand,
   *  staff, scroll), resolved by ability-use.ts; absent / null = not a charged cast. */
  chargedSource?: import('./charged-cast.js').ItemCast | null;
  /** …and the cast-from-item fact while ITS dispatch runs (janusCore sets it for the charged cast's own dispatch only — a nested janus() of
   *  a trigger / payload is not cast from the item). Read by the mana skip, the charge bill, the scroll multiplier and casting skill. */
  itemCast?: import('./charged-cast.js').ItemCast | null;
  /** At-will/innate inventory provenance for THIS dispatch's casting skill, independent of charged-item billing. */
  spellGrantFromInventory?: boolean;
  /** Wizard v9 WP5 (owner 2026-10-09, CONSUME SCROLLS ON CAST): the scroll row this use drained to 0 — one unit is removed by the commit's
   *  `charged_source.consume` (charged-cast.ts settleSourceCharges). */
  chargedConsume?: { inventoryId: string } | null;
  /** PRE-DRAW DRY RUN ONLY (ability-use.ts): force the ranged natural-1 stray pick onto this candidate, and tell the synthetic
   * roll source the pick happened (it switches from "every face 1" to the probed extreme for the re-roll onward). */
  strayProbe?: { candidateId: string; picked: () => void } | null;
  /** PRE-DRAW DRY RUN ONLY (ability-use.ts sets it on every synthetic-dice clone): the random-table payload (random-table-payload.ts)
   *  then also probes EVERY outcome its table can hand out (at the state that run reached), so an outcome-dependent hand-back is decided before\n   *  the real draw. */
  dryRun?: boolean;
  /** DRY RUNS (shared by a use's dry runs): every random table the use rolled → the attachments its ranges hand out — the caller then re-runs the
   *  WHOLE use once per outcome with 
andomTableForced (random-table-payload.ts), so an outcome's later gates are decided pre-draw too. */
  randomTableSeen?: Map<string, Array<string | null>>;
  /** DRY RUN ONLY: this run's table rolls the first range attaching ttached (null = nothing) instead of drawing. */
  randomTableForced?: { tableId: string; attached: string | null } | null;
  /** R-128 (lane A3): the ENGINE-FIRED cast in progress (a contingency firing at a combat boundary — ft_contingency_fire_cast): THIS spell on THIS
   *  owner spends no AP and no mana (pre-paid by the arming); a reaction slot is still spent; a cast nested inside it pays as usual. */
  engineFiredCast?: { casterId: string; abilityId: string } | null;
  /** A due damage arm is attempted once per frame, including a fire that stays armed after a skip. */
  contingencyAttempts?: Set<string>;
  /** Lane A8: the members whose PRE-ROLL Evade fired this use (applyPreRollEvade). PHP applies Evade as a STATUS on its holder that stays live
   *  until a next-roll-against consumes it; the browser never writes that row (net zero in the use), but a later is_target roll of the holder
   *  this use (the Maimed-on-crit save) reads its roll_type_against_dis and consumes it (skill-roll.ts rollTypeModifierStacks). */
  preRollEvadeStatus?: Set<string>;
  /** Wizard v9 WP3 (summon.ts): the summons this use decided — same CAPABILITY idiom as deathTail: present (even empty) = the caller's commit
   *  persists them (ability_truth_commit_inc.php `summons`); absent → a summon hands back. */
  summons?: import('./summon.js').SummonPlan[] | null;
  spawnRows?: import('../spawn/row-plan').SpawnRowPlan;
  composedDeaths?: string[];
  spawnTurnTransition?: import('../turns/end-turn').TurnDeathTransition;
  /** The page's summon placement for this use (the cast point + one banked spot per summon); absent = no ground placement. */
  summonRequest?: import('./summon.js').SummonRequest | null;
  /** The use whose summons the commit persists (its ability + caster): a summon decided in any other dispatch hands back (summon.ts). */
  summonOrigin?: { abilityId: string; casterId: string } | null;
  /** Roll dispatches reached outside a trigger frame (janusDispatchRollTypes / noRoll): the cast WENT OFF — past armor bending, a hostile
   *  miscast and the counterspell check, with a valid target. A corpse use fires only for a cast that went off (summon.ts). */
  castsWentOff?: number;
  /** summon_roll's result for the post-effect summon loop (summon-roll.ts) — how many creatures this target's pass raises. */
  summonRollResult?: number | null;
  /** OWNER RULING 2026-10-03 (Fortunate — trigger-consent.ts deferFortunateOffer): the PLAYER-controlled members whose luck gain this use
   *  did NOT spend on the universal Fortunate inline; the page offers it to their controllers after the commit. */
  fortunateOffers?: Set<string>;
}
/** janus_add_status_stack(): an existing quantity-family row's new count. `before` is the stored count the commit
 * compares-and-swaps against; `status_stack` the capped count after every increment this use made. */
export interface StatusStackChange { status_id: string; status_item_id: string; target: string; before: number; status_stack: number }

export const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
export const scalar = (value: unknown): unknown => { let v = value; let depth = 0; while (Array.isArray(v)) {
  if (++depth > 32) handBack('janus:cyclic-scalar');
  v = v[0];
} return v; };
export const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
export const int = (value: unknown): number => {
  const v = scalar(value);
  if (v == null) handBack('janus:invalid-numeric-layer');
  if ((typeof v !== 'number' && typeof v !== 'string') || v === ''
      || (typeof v === 'string' && !/^-?\d+$/.test(v))) handBack('janus:invalid-numeric-layer');
  const n = Number(v);
  if (!Number.isSafeInteger(n)) handBack('janus:invalid-numeric-layer');
  return n;
};
/** Required state must never inherit Number(null), Number(''), or a coercion fallback. */
export function requiredInt(value: unknown, reason: string): number {
  const v = scalar(value);
  if ((typeof v !== 'number' && typeof v !== 'string') || v === '' || (typeof v === 'string' && !/^-?\d+$/.test(v)))
    handBack(reason);
  const n = Number(v);
  if (!Number.isSafeInteger(n)) handBack(reason);
  return n;
}

/** item_field() twin: `items.<key>` on a definition, or the row's own key. */
export function itemField(row: Row | null, key: string): unknown {
  const items = record(row?.items);
  return scalar(items?.[key] ?? row?.[key]);
}
/** The encounter_creatures bucket of a member row (created when absent). */
export function ec(row: Row): Row {
  let bucket = record(row.encounter_creatures);
  if (!bucket) { bucket = {}; row.encounter_creatures = bucket; }
  return bucket;
}
export function ecId(row: Row | null): string { return upper(record(row?.encounter_creatures)?.encounter_creature_id); }
const UUID_RE = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
/** inventory_field(row, 'inventory_id') — the `inventory` bucket (denested), else a flat copy. */
export function inventoryId(row: Row | null): string {
  return upper(record(row?.inventory)?.inventory_id ?? row?.inventory_id);
}
/** target_arr_is_inventory_target(): an encounter inventory row (a UUID inventory_id, no encounter_creatures identity). */
export function isInventoryRow(row: Row | null): boolean { return !ecId(row) && UUID_RE.test(inventoryId(row)); }
/** insert_roll()'s roll_by / roll_to identity: the encounter_creature_id, or an inventory row's inventory_id. */
export function actorId(row: Row | null): string { return ecId(row) || (isInventoryRow(row) ? inventoryId(row) : ''); }
/** Statuses visible to later steps of this same cast, including pending edits. */
export function activeStatuses(ctx: JanusCtx, creature: Row): Row[] {
  const target = actorId(creature);
  return [...ctx.read.statuses(creature),
    ...ctx.statusesAdded.filter((added) => added.target === target).map((added) => added as unknown as Row)]
    .filter((status) => !ctx.statusesRemoved.some(r => upper(r.target) === target
      && (r.status_id ? upper(r.status_id) === upper(status.status_id) : upper(r.status_item_id) === upper(status.status_item_id))));
}
/** A status row THIS dispatch added (still "pending" in PHP's in-memory frame) — not one an earlier dispatch of the same use added. */
export function isPendingThisDispatch(ctx: JanusCtx, status: unknown): boolean {
  return ctx.statusesAdded.includes(status as StatusRow) && !(ctx.priorDispatchStatuses?.has(status as StatusRow) ?? false);
}
/** The member row's creature item_type ('player_character', 'creature', 'lair', …). */
export function creatureItemType(row: Row): string { return String(itemField(row, 'item_type') ?? record(row.encounter_creatures)?.item_type ?? ''); }

/** The truth commits' die-face contract (ability_truth_commit_inc.php / turn_truth_commit_inc.php — keep in step): an int, or a
 * short display token (a forced / scaling / defence / half / gate symbol, a "-N" reduction step, a numeric-string modifier such as
 * healing_roll's "4"): 1–24 characters, no markup or control characters. A face outside it is refused AFTER the page drew, so every
 * resolver checks it on its dry runs (pre-draw). */
const FACE_FORBIDDEN = '<>&"\'';
export function commitFaceAccepted(value: unknown): boolean {
  return Number.isSafeInteger(value) || (typeof value === 'string' && value.length >= 1 && value.length <= 24
    && ![...value].some((ch) => FACE_FORBIDDEN.includes(ch) || ch.charCodeAt(0) < 32));
}
/** Lane A17 (2026-10-09, EC1 CE1 / MW1c — the '' vs NULL log subtype): the STRING keys legacy wrote through insert_roll (roll_type_2 ''
 *  when empty). A UUID roll type is insert_roll too; every other string key was insert_log_message, which stores roll_type_2 NULL. The PAGE
 *  decides (this list); the commits store roll_type_2 as posted (includes/functions/db/rolls/truth_row_type_2.php). */
const INSERT_ROLL_STRING_KEYS: ReadonlySet<string> = new Set(['add_inventory', 'adjust_charges', 'ap_change_during_turn', 'ap_change_to_caster_during_turn',
  'ascension_skill_tier_up', 'awarded_experience_marks', 'can_level_up', 'casting', 'casting_ritual', 'change_charges', 'creature_fell', 'creature_sank',
  'damage_heals_caster', 'disenchanting', 'essence', 'essence_appraised', 'failed_roll_for_ascension_skill_point', 'finish_reading',
  'gained_ascension_skill_point', 'gathering', 'gm_award_luck', 'gm_pulled_campaign_time', 'gm_pushed_campaign_time', 'healing_damages_caster',
  'light_source_kindle', 'no_roll', 'post_effect_condition_failed', 'prepare_spell_list', 'random_creature', 'remarkable_crit_trigger_d20',
  'reset_prepared_spell_list', 'speed_type_increased', 'speed_type_moved', 'start_training', 'status_effect_moved', 'teleport_cleared_conditions',
  'training_roll', 'training_skill_tier_up', 'transfer_damage_percentage_to_caster_of_status_effect', 'weapon_attack_count_change', 'wind_wall_lift',
  'world_shaking_crit_trigger_d20']);
const UUID_KEY = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;
/** True when legacy logged this key through insert_log_message (roll_type_2 NULL) — a random table's own row (its item type) is insert_roll. */
export function isLogMessageKey(type1: string): boolean {
  return type1 !== '' && !UUID_KEY.test(type1) && !INSERT_ROLL_STRING_KEYS.has(type1)
    && !(type1.startsWith('random_') && !type1.includes('_applied'));
}
/** insert_roll() / insert_log_message() — appends one log row. `by` / `to` are member rows (or null). An empty subtype posts '' for an
 *  insert_roll row and null for a log-message row (`logMessage`, default isLogMessageKey). */
export function insertRoll(ctx: JanusCtx, row: {
  item_display?: LogRow['item_display'];
  roll_type_1: string; roll_type_2?: string | null; roll_item_id?: string | null; by?: Row | null; to?: Row | null;
  roll_result?: number; rolls_full?: unknown[] | string | null; dice?: number; die?: number; mod?: number; adv_dis_stacks?: number; dc?: number;
  feedback?: RollFeedback | null;
  random_table?: LogRow['random_table'];
  house_rule?: CritHouseRule;
  trace?: readonly AdvDisTraceEntry[];
  log_item_target?: LogRow['log_item_target'];
  logMessage?: boolean;
}): void {
  const emptySubtype = row.roll_type_2 == null || row.roll_type_2 === '';
  const log: LogRow = {
    roll_type_1: row.roll_type_1,
    roll_type_2: emptySubtype ? ((row.logMessage ?? isLogMessageKey(row.roll_type_1)) ? null : '') : row.roll_type_2!,
    roll_item_id: row.roll_item_id ? upper(row.roll_item_id) : null,
    roll_by: row.by ? actorId(row.by) || null : null,
    roll_to: row.to ? actorId(row.to) || null : null,
    roll_result: row.roll_result ?? 0,
    rolls_full: row.rolls_full ?? null,
    dice: { dice: row.dice ?? 0, die: row.die ?? 0, mod: row.mod ?? 0, adv_dis_stacks: row.adv_dis_stacks ?? 0, dc: row.dc ?? 0 },
    ...(row.feedback ? { feedback: row.feedback } : {}),
    ...(row.random_table !== undefined ? { random_table: row.random_table } : {}),
    ...(row.log_item_target ? { log_item_target: row.log_item_target } : {}),
    ...(row.house_rule ? { house_rule: row.house_rule } : {}),
    ...(row.item_display ? { item_display: row.item_display } : {}),
  };
  if (row.trace?.length) rollTraces.set(log, row.trace);
  ctx.rows.push(log);
}

/** The encounter_creatures columns that changed on every member, AFTER values (the verdict's `ec`). */
export function ecChanges(ctx: JanusCtx): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {};
  for (const [id, row] of ctx.members) {
    const original = ctx.originals.get(id);
    const before = record(original?.encounter_creatures) ?? {};
    const after = record(row.encounter_creatures) ?? {};
    for (const key of Object.keys(after)) {
      if (!EC_COLUMNS.has(key)) continue;
      if (key === 'x_pos' || key === 'y_pos' || key === 'z_pos') {
        const previous = original ? posCoord(original, key) ?? 0 : 0;
        const previousCell = encounterCreatureField(record(original?.encounter_creatures), key);
        const nextCell = encounterCreatureField(after, key);
        const changed = String(previousCell ?? '') !== String(nextCell ?? '');
        const next = changed ? nextCell : posCoord(row, key) ?? 0;
        if (String(previous) !== String(next)) (out[id] ??= {})[key] = next;
        continue;
      }
      // orb_queue is a JSON LIST column: the compiled row holds the decoded list, a write holds the column text — compare and emit
      // the SQL text (scalar() would read only the first orb).
      if (key === 'orb_queue') {
        const text = orbQueueText(after[key]);
        if (orbQueueText(before[key]) !== text) (out[id] ??= {})[key] = text;
        continue;
      }
      const previous = original && EC_LEAN_ZERO.has(key) ? ecLean(original, key) : before[key];
      if (String(scalar(previous) ?? '') !== String(scalar(after[key]) ?? '')) (out[id] ??= {})[key] = scalar(after[key]);
    }
  }
  return out;
}

/** encounter_creatures table columns a use may write (the oracle diffs exactly these). */
export const EC_COLUMNS = new Set(['v_current', 'v_max', 'v_temp', 'v_max_temp', 'encounter_creature_dead', 'encounter_creature_private', 'aps', 'aps_total', 'weapon_attack', 'head_attack',
  'reaction', 'mana', 'luck', 'spirit', 'hunger_meter_current', 'thirst_meter_current', 'x_pos', 'y_pos', 'z_pos', 'movement_pct', 'preferred_speed_type',
  'spell_list_prepared', 'orb_queue', 'team', 'controller_encounter_creature_id', 'creature_initiative']);
